import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import EmergencyWarning from '../components/EmergencyWarning';
import { 
  Video, 
  MessageSquare, 
  Pill, 
  Shield, 
  Clock, 
  Heart,
  ArrowRight,
  CheckCircle,
  Smartphone,
  Download,
  Calendar
} from 'lucide-react';

const LandingPage = () => {
  const services = [
    {
      icon: Video,
      title: 'Virtual Urgent Care',
      description: 'Connect with providers from home via secure video consultation',
      duration: '20 min'
    },
    {
      icon: MessageSquare,
      title: 'Follow-Up Consultation',
      description: 'Quick check-ins for existing patients via secure messaging',
      duration: '15 min'
    },
    {
      icon: Pill,
      title: 'Async Refill Request',
      description: 'Message-based refill requests with quick pharmacy delivery',
      duration: 'Async'
    }
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Download Spruce Health',
      description: 'Get the Spruce Health app and connect with Integrated Wellness'
    },
    {
      step: '02',
      title: 'Create Your Account',
      description: 'Register and upload your insurance card, photo ID, and documents'
    },
    {
      step: '03',
      title: 'Book & Pay',
      description: 'We\'ll send you the calendar link and payment options via Spruce'
    },
    {
      step: '04',
      title: 'Attend Your Visit',
      description: 'Join your video consultation. *Some services are asynchronous!'
    }
  ];

  const features = [
    { icon: Shield, text: 'HIPAA Compliant & Secure' },
    { icon: Clock, text: 'Same-Day Appointments, Schedule Permitting' },
    { icon: Smartphone, text: 'Spruce Health App Required' },
    { icon: Pill, text: 'Quick Pharmacy Delivery' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - IWMC Style */}
      <section className="relative min-h-screen overflow-hidden bg-white">
        {/* Background Logo Watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-100" style={{ zIndex: 0 }}>
          <img 
            alt="" 
            className="w-[120vmin] h-[120vmin] object-contain"
            src="https://customer-assets.emergentagent.com/job_53640627-9830-4adc-91a5-529f85e3f30c/artifacts/pm4tpcen_IWMC_logo_transparent.png"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-48 pb-20">
          {/* Main Hero Content */}
          <div className="flex flex-col lg:flex-row items-center justify-center gap-12 mb-16">
            <div className="bg-white/95 backdrop-blur-sm px-10 py-10 rounded-2xl shadow-xl border-2 border-[#1E3A8A] text-center max-w-3xl">
              <h1 className="text-5xl lg:text-6xl xl:text-7xl font-black text-[#1E3A8A] leading-tight mb-6" style={{ fontFamily: 'Merriweather, serif' }}>
                Integrated Virtual Urgent Care,<br/>Your Way
              </h1>
              <p className="text-2xl lg:text-3xl text-[#1E3A8A] mb-2 font-black" style={{ fontFamily: 'Merriweather, serif' }}>
                Telemedicine for NY & NJ — powered by the Integrated Matrix Engine, Patent Pending
              </p>
              <p className="text-xl text-[#3B82F6] mb-8 font-black" style={{ fontFamily: 'Merriweather, serif' }}>
                *More States Coming Soon!
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="https://spruce.care/integratedwellness" target="_blank" rel="noopener noreferrer">
                  <button 
                    className="inline-block text-white text-2xl font-black px-12 py-5 rounded-lg transition-all shadow-lg"
                    style={{ 
                      background: 'linear-gradient(to right, #DC2626, #B91C1C)',
                      fontFamily: 'Merriweather, serif'
                    }}
                    data-testid="hero-download-button"
                  >
                    Download Spruce Health App →
                  </button>
                </a>
                <a 
                  href="https://gamma.app/docs/Virtual-Urgent-Care-See-a-Provider-Today-u874i0csh14ejts" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <button 
                    className="inline-block text-white text-2xl font-black px-12 py-5 rounded-lg transition-all shadow-lg"
                    style={{ 
                      background: 'linear-gradient(to right, #1E3A8A, #3B82F6)',
                      fontFamily: 'Merriweather, serif'
                    }}
                  >
                    Explore Integrated Urgent Care →
                  </button>
                </a>
              </div>
              <div className="flex justify-center mt-4">
                <Link to="/chatbot">
                  <button 
                    className="inline-flex items-center gap-2 text-[#1E3A8A] bg-white text-xl font-black px-8 py-4 rounded-lg transition-all shadow-lg hover:bg-white/90"
                    style={{ fontFamily: 'Merriweather, serif' }}
                  >
                    <MessageSquare className="w-6 h-6" />
                    Chat with AI Assistant
                  </button>
                </Link>
              </div>
            </div>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-wrap justify-center gap-6 mb-12">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3 text-base font-black bg-white/95 px-5 py-3 rounded-full border-2 border-[#1E3A8A] shadow-lg">
                <feature.icon className="w-6 h-6 text-[#3B82F6]" />
                <span className="text-slate-800" style={{ fontFamily: 'Merriweather, serif' }}>{feature.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Spruce Health App Section - MANDATORY */}
      <section className="py-16 bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-base font-black mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
                <Smartphone className="w-4 h-4" />
                REQUIRED FOR ALL PATIENTS
              </div>
              <h2 className="text-3xl lg:text-4xl font-black mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
                Download Spruce Health App
              </h2>
              <p className="text-white/90 text-xl mb-6 font-black" style={{ fontFamily: 'Merriweather, serif' }}>
                Our telemedicine service uses Spruce Health for all patient communication, 
                video visits, and secure document uploads. You <strong>MUST</strong> download 
                and register on Spruce before your first appointment.
              </p>
              <div className="flex flex-wrap gap-4 mb-6">
                <a 
                  href="https://spruce.care/integratedwellness" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-[#1E3A8A] px-8 py-4 rounded-lg font-black text-xl hover:bg-white/90 transition-colors shadow-lg"
                  style={{ fontFamily: 'Merriweather, serif' }}
                >
                  <Download className="w-6 h-6" />
                  Download Spruce Health App
                </a>
              </div>
              <p className="text-white/70 text-base font-black" style={{ fontFamily: 'Merriweather, serif' }}>
                Click the button above to connect directly with Integrated Wellness Medical Center on Spruce.
              </p>
            </div>
            <div className="hidden lg:block">
              <img 
                src="https://customer-assets.emergentagent.com/job_wellness-telecare/artifacts/35qvd53e_xEeaDYOX-fbBqYEIIvo-9.png"
                alt="Telemedicine video call on smartphone"
                className="rounded-2xl shadow-2xl w-full max-w-md mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Warning */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <EmergencyWarning />
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-100">
          <img 
            alt="" 
            className="w-[120vmin] h-[120vmin] object-contain"
            src="https://customer-assets.emergentagent.com/job_53640627-9830-4adc-91a5-529f85e3f30c/artifacts/pm4tpcen_IWMC_logo_transparent.png"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-white/95 backdrop-blur-sm px-8 py-6 rounded-2xl shadow-xl border-2 border-[#1E3A8A]">
              <h2 className="text-3xl lg:text-4xl font-black text-[#1E3A8A] mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
                Choose Your Visit Type
              </h2>
              <p className="text-slate-700 text-xl font-black" style={{ fontFamily: 'Merriweather, serif' }}>
                Flexible virtual care options designed around your schedule
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-white/95 backdrop-blur-sm p-8 rounded-2xl shadow-xl border-2 border-[#1E3A8A] hover:shadow-2xl transition-all text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#3B82F6] to-[#1E3A8A] rounded-xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <div className="inline-block px-4 py-1 bg-[#3B82F6]/20 text-[#1E3A8A] text-base font-black rounded-full mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
                  {service.duration}
                </div>
                <h3 className="text-xl font-black text-[#1E3A8A] mb-3" style={{ fontFamily: 'Merriweather, serif' }}>{service.title}</h3>
                <p className="text-slate-700 font-black" style={{ fontFamily: 'Merriweather, serif' }}>{service.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a href="https://spruce.care/integratedwellness" target="_blank" rel="noopener noreferrer">
              <button 
                className="inline-block text-white text-xl font-black px-10 py-4 rounded-lg transition-all shadow-lg"
                style={{ 
                  background: 'linear-gradient(to right, #DC2626, #B91C1C)',
                  fontFamily: 'Merriweather, serif'
                }}
              >
                Download Spruce Health App
                <ArrowRight className="w-5 h-5 inline ml-2" />
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* How It Works - 4 Steps */}
      <section className="py-20 bg-slate-50 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-100">
          <img 
            alt="" 
            className="w-[120vmin] h-[120vmin] object-contain"
            src="https://customer-assets.emergentagent.com/job_53640627-9830-4adc-91a5-529f85e3f30c/artifacts/pm4tpcen_IWMC_logo_transparent.png"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-white/95 backdrop-blur-sm px-8 py-6 rounded-2xl shadow-xl border-2 border-[#1E3A8A]">
              <h2 className="text-3xl lg:text-4xl font-black text-[#1E3A8A] mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
                How It Works
              </h2>
              <p className="text-slate-700 text-xl font-black" style={{ fontFamily: 'Merriweather, serif' }}>
                Getting care has never been easier — four simple steps
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((item, index) => (
              <div 
                key={index}
                className="bg-white/95 backdrop-blur-sm p-6 rounded-2xl shadow-xl border-2 border-[#1E3A8A] text-center hover:shadow-2xl transition-shadow"
              >
                <span className="text-5xl font-black text-[#3B82F6]/60 block mb-2" style={{ fontFamily: 'Merriweather, serif' }}>{item.step}</span>
                <h3 className="text-xl font-black text-[#1E3A8A] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>{item.title}</h3>
                <p className="text-slate-700 text-base font-black" style={{ fontFamily: 'Merriweather, serif' }}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pharmacy Delivery */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-100">
          <img 
            alt="" 
            className="w-[120vmin] h-[120vmin] object-contain"
            src="https://customer-assets.emergentagent.com/job_53640627-9830-4adc-91a5-529f85e3f30c/artifacts/pm4tpcen_IWMC_logo_transparent.png"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="bg-white/95 backdrop-blur-sm p-4 rounded-2xl shadow-xl border-2 border-[#1E3A8A]">
              <img 
                src="https://customer-assets.emergentagent.com/job_wellness-telecare/artifacts/9zty3e4i_bqhDfzEs1ClSjUTlcVx5n.png"
                alt="Pharmacy delivery service"
                className="rounded-xl w-full object-cover"
              />
            </div>
            <div className="space-y-6">
              <div className="inline-block bg-white/95 backdrop-blur-sm px-6 py-4 rounded-2xl shadow-xl border-2 border-[#1E3A8A]">
                <div className="inline-flex items-center gap-2 text-[#3B82F6] text-base font-black mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                  <Pill className="w-4 h-4" />
                  NY & NJ Delivery
                </div>
                <h2 className="text-3xl lg:text-4xl font-black text-[#1E3A8A]" style={{ fontFamily: 'Merriweather, serif' }}>
                  Quick Pharmacy Delivery
                </h2>
              </div>
              <div className="bg-white/95 backdrop-blur-sm px-6 py-5 rounded-2xl shadow-xl border-2 border-[#1E3A8A]">
                <p className="text-[#1E3A8A] text-xl font-black" style={{ fontFamily: 'Merriweather, serif' }}>
                  Skip the pharmacy lines. Your prescriptions are sent electronically to our partner pharmacy, 
                  Bell Pharmacy, and delivered quickly to your door throughout New York and New Jersey.
                </p>
                <p className="text-[#3B82F6] text-base font-black mt-3" style={{ fontFamily: 'Merriweather, serif' }}>
                  ✨ More states coming soon!
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  'Electronic prescriptions sent instantly',
                  'Bell Pharmacy partnership',
                  'Serving all of NY & NJ',
                  'Real-time delivery tracking'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2 bg-white/95 p-3 rounded-lg border-2 border-[#1E3A8A]">
                    <CheckCircle className="w-5 h-5 text-[#3B82F6] flex-shrink-0" />
                    <span className="text-base font-black text-[#1E3A8A]" style={{ fontFamily: 'Merriweather, serif' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs CTA */}
      <section className="py-20 bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-black mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
              Beyond Urgent Care
            </h2>
            <p className="text-white/90 text-xl font-black max-w-3xl mx-auto" style={{ fontFamily: 'Merriweather, serif' }}>
              Our comprehensive wellness programs are designed to optimize your health and help you achieve lasting transformation.
            </p>
          </div>

          {/* Program Cards with Images */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              {
                name: 'Optimization Program',
                image: 'https://customer-assets.emergentagent.com/job_care-connect-157/artifacts/d13vtcqt_PATFZmgClN63d0DDI4iEy.png'
              },
              {
                name: 'Weight Loss',
                image: 'https://customer-assets.emergentagent.com/job_care-connect-157/artifacts/f06snpds_Y-xURBQ4CcunUebNZCLrn.png'
              },
              {
                name: 'Hormone Optimization',
                image: 'https://customer-assets.emergentagent.com/job_care-connect-157/artifacts/7slqjh3k_A72gN1Ma4BZe6yO1wKZDD.png'
              },
              {
                name: 'Longevity',
                image: 'https://customer-assets.emergentagent.com/job_care-connect-157/artifacts/7x5n8ul9_jDMi0h2u6U9xU8DFQydkO.png'
              },
              {
                name: 'Mental Health',
                image: 'https://customer-assets.emergentagent.com/job_care-connect-157/artifacts/ex7wp8a3_-focZ9cGf3SoN8Qon2pDa.png'
              },
              {
                name: 'Life Coaching',
                image: 'https://customer-assets.emergentagent.com/job_care-connect-157/artifacts/cyhvu17q_BifZLduCBh4THB8pMEDmU.png'
              }
            ].map((program, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow"
              >
                <div className="aspect-video relative overflow-hidden">
                  <img 
                    src={program.image}
                    alt={program.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A8A]/80 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <h3 className="text-xl font-black" style={{ fontFamily: 'Merriweather, serif' }}>{program.name}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register-program">
              <button 
                className="inline-block bg-white text-[#1E3A8A] text-xl font-black px-8 py-4 rounded-lg transition-all shadow-lg hover:bg-white/90"
                style={{ fontFamily: 'Merriweather, serif' }}
                data-testid="register-program-cta"
              >
                Register for a Program
                <ArrowRight className="w-5 h-5 inline ml-2" />
              </button>
            </Link>
            <Link to="/programs">
              <button 
                className="inline-block bg-white/20 border-2 border-white text-white text-xl font-black px-8 py-4 rounded-lg transition-all hover:bg-white/30"
                style={{ fontFamily: 'Merriweather, serif' }}
              >
                View All Programs
              </button>
            </Link>
            <a href="https://iwumm.com" target="_blank" rel="noopener noreferrer">
              <button 
                className="inline-block bg-white/20 border-2 border-white text-white text-xl font-black px-8 py-4 rounded-lg transition-all hover:bg-white/30"
                style={{ fontFamily: 'Merriweather, serif' }}
              >
                Visit IWUMM.com
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-100">
          <img 
            alt="" 
            className="w-[120vmin] h-[120vmin] object-contain"
            src="https://customer-assets.emergentagent.com/job_53640627-9830-4adc-91a5-529f85e3f30c/artifacts/pm4tpcen_IWMC_logo_transparent.png"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-white/95 backdrop-blur-sm px-10 py-8 rounded-2xl shadow-xl border-2 border-[#1E3A8A]">
            <h2 className="text-3xl lg:text-4xl font-black text-[#1E3A8A] mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
              Ready to Get Started?
            </h2>
            <p className="text-slate-700 text-xl mb-8 font-black" style={{ fontFamily: 'Merriweather, serif' }}>
              Download Spruce Health now or chat with our AI assistant to find the right care for you.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://spruce.care/integratedwellness" target="_blank" rel="noopener noreferrer">
                <button 
                  className="inline-block text-white text-xl font-black px-10 py-4 rounded-lg transition-all shadow-lg"
                  style={{ 
                    background: 'linear-gradient(to right, #DC2626, #B91C1C)',
                    fontFamily: 'Merriweather, serif'
                  }}
                  data-testid="final-cta-download"
                >
                  Download Spruce Health App
                  <ArrowRight className="w-5 h-5 inline ml-2" />
                </button>
              </a>
              <a href="tel:2015353582">
                <button 
                  className="inline-block text-[#1E3A8A] border-2 border-[#1E3A8A] text-xl font-black px-10 py-4 rounded-lg transition-all hover:bg-[#1E3A8A]/5"
                  style={{ fontFamily: 'Merriweather, serif' }}
                >
                  Call (201) 535-3582
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
